import {useId, startTransition, useState} from 'react'
import type {Icon} from '@primer/octicons-react'
import {
  AlertIcon,
  CheckCircleFillIcon,
  GitMergeIcon,
  CheckIcon,
  XIcon,
  ExclamationIcon,
  CircleIcon,
  GitPullRequestIcon,
  StopIcon,
} from '@primer/octicons-react'
import CircleOcticon from '@github-ui/circle-octicon'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {useAnalytics} from '@github-ui/use-analytics'
import {useQueryClient} from '@github-ui/react-query'
import {Banner, BranchName, Button, Flash, Label, type LabelColorOptions, NavList, Spinner} from '@primer/react'

import styles from './StackSection.module.css'
import {MergeBoxExpandable} from './common/MergeBoxExpandable'
import {MergeBoxSectionHeader} from './common/MergeBoxSectionHeader'
import {CenteredLoadingSpinner} from './common/CenteredLoadingSpinner'
import {HEADER_ICON_SIZE} from '../../constants'
import type {
  StackSectionStatusType,
  StackSectionEntryType,
  StackSectionEntryStatus,
} from '../../helpers/merge-box-status-calculator/stack-section-status'
import {
  useStackRebaseMutation,
  useStackRebaseStatus,
  type StackRebasePullRequestStatus,
} from '../../hooks/mutations/use-stack-rebase-mutation'
import {useMergeBoxPageDataQueryKey} from '../../page-data/loaders/use-merge-box-page-data'
import type {AliveChannels as MergeBoxAliveChannels} from '../../page-data/payloads/merge-box'
import type {AliveChannels as StatusChecksAliveChannels} from '../../page-data/payloads/status-checks'
import {useMergeabilityLiveUpdates} from '../../hooks/use-mergeability-live-updates'
import {useStackMergeabilityQuery} from '../../page-data/loaders/use-stack-mergeability-page-data'
import {useMergeMethodContext} from '../../contexts/MergeMethodContext'

export function StackSection({
  pullRequestId,
  status,
  entries,
  baseRefName,
  showRebaseButton = true,
}: {
  pullRequestId: string
  status: StackSectionStatusType
  entries: StackSectionEntryType[]
  baseRefName?: string
  showRebaseButton?: boolean
}) {
  const stackSectionAriaId = useId()
  const [stackExpanded, setStackExpanded] = useSessionStorage<boolean>(
    `${pullRequestId}:stackExpanded`,
    status !== 'PASSED',
  )
  const {isRebasing, status: rebaseState} = useStackRebaseStatus()
  const [hideRebaseSummary, setHideRebaseSummary] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const showRebaseSummary = Object.keys(rebaseState).length > 0 && !isRebasing && !hideRebaseSummary

  const {sendAnalyticsEvent} = useAnalytics()
  const mergeBoxQueryKey = useMergeBoxPageDataQueryKey()
  const queryClient = useQueryClient()

  const onSuccessfulRebase = async () => {
    await queryClient.refetchQueries({queryKey: mergeBoxQueryKey}, {cancelRefetch: false})
  }

  const handleRebaseError = (e: Error) => {
    setErrorMessage(e.message)
  }

  const {mutate: rebaseStack} = useStackRebaseMutation({
    onSuccess: onSuccessfulRebase,
    onError: handleRebaseError,
  })

  const handleRebaseStack = () => {
    if (isRebasing) return
    setErrorMessage(null)
    rebaseStack({entries})
    sendAnalyticsEvent('conflicts_section.update_branch', 'MERGEBOX_CONFLICTS_SECTION_UPDATE_BRANCH_BUTTON')
  }

  const otherEntriesToMerge = entries.filter(e => e.position === 'BEFORE' && e.pull.state !== 'MERGED')

  let title
  let subtitle

  if (isRebasing) {
    title = 'Rebasing stack…'

    if (rebaseState) {
      const count = Object.values(rebaseState).length
      subtitle = `Rebasing ${count} pull request${count === 1 ? '' : 's'}.`
    } else {
      subtitle = 'Starting rebase.'
    }
  } else if (status === 'FAILED' || status === 'BLOCKED') {
    title = 'Unable to merge as stack'
    subtitle = 'Some pull requests in the stack cannot be merged.'
  } else if (status === 'PASSED') {
    title = 'Ready to merge as stack'
    const count = otherEntriesToMerge.length
    if (count === 0) {
      subtitle = 'This is the first unmerged pull request in the stack.'
    } else {
      subtitle = `Merging this pull request will also merge ${count} pull request${count === 1 ? '' : 's'} below it.`
    }
  } else {
    title = 'Could not determine stack status'
    subtitle = 'Refresh the page to try again.'
  }

  return (
    <section
      aria-label="Stacked pull requests"
      aria-describedby={stackSectionAriaId}
      className="border-bottom color-border-subtle"
    >
      <MergeBoxSectionHeader
        headerId={stackSectionAriaId}
        title={title}
        subtitle={subtitle}
        icon={
          isRebasing ? (
            <CenteredLoadingSpinner className={styles.StackSectionLoadingSpinner} />
          ) : (
            <StatusIcon status={status} />
          )
        }
        {...(showRebaseButton
          ? {
              rightSideContent: (
                <Button loading={isRebasing} loadingAnnouncement="Rebasing stack" onClick={handleRebaseStack}>
                  Rebase stack
                </Button>
              ),
            }
          : {
              expandableProps: {
                ariaLabel: stackExpanded ? 'Collapse stack details' : 'Expand stack details',
                isExpanded: stackExpanded,
                onToggle: () => {
                  setStackExpanded(!stackExpanded)
                },
              },
            })}
      />
      <MergeBoxExpandable isExpanded={showRebaseButton || stackExpanded}>
        <NavList className={styles.stackNavList}>
          {entries.map(e => (
            <NavList.Item key={e.pull.number} href={e.pull.url} aria-current={e.position === 'CURRENT'}>
              <NavList.LeadingVisual>
                <EntryStatusIcon entry={e} rebaseState={rebaseState[e.pull.number]} />
              </NavList.LeadingVisual>
              <span className={e.position === 'AFTER' ? 'fgColor-muted' : ''}>{e.pull.title}</span>{' '}
              <span className="fgColor-muted">#{e.pull.number}</span>
              <NavList.TrailingVisual>
                <EntryStatusLabel entry={e} rebaseState={rebaseState[e.pull.number]} />
              </NavList.TrailingVisual>
              {!!e.channels && <EntryMergeabilityUpdateListener basePageDataUrl={e.pull.url} {...e.channels} />}
            </NavList.Item>
          ))}
          {baseRefName && (
            <li className={styles.branchItem}>
              <span className={styles.branchItemContent}>
                <CircleIcon size={12} className="fgColor-muted" />
                <BranchName as="span">{baseRefName}</BranchName>
              </span>
            </li>
          )}
        </NavList>
      </MergeBoxExpandable>
      {showRebaseSummary && (
        <RebaseSummaryBanner rebaseState={rebaseState} onDismiss={() => setHideRebaseSummary(true)} />
      )}
      {errorMessage && (
        <Flash className="tmp-m-3" variant="danger" role="alert">
          <StopIcon className="mr-2" />
          {errorMessage}
        </Flash>
      )}
    </section>
  )
}

function EntryMergeabilityUpdateListener({
  basePageDataUrl,
  ...channels
}: {
  basePageDataUrl: string
} & MergeBoxAliveChannels &
  StatusChecksAliveChannels) {
  const {mergeMethod} = useMergeMethodContext()
  const {refetch} = useStackMergeabilityQuery({basePageDataUrl, mergeMethod, enabled: false})
  useMergeabilityLiveUpdates({
    channels,
    refetchQuery: () => {
      startTransition(() => {
        refetch()
      })
    },
    throttleTime: 5000,
  })
  return null
}

type EntryIconInfo = {type: 'icon'; icon: Icon; className: string; ariaLabel: string} | {type: 'spinner'}

type EntryStatusInfo = {
  label: {variant: LabelColorOptions; text: string} | null
  icon: EntryIconInfo
}

const ENTRY_STATUS_INFO: Record<StackSectionEntryStatus, EntryStatusInfo> = {
  IRRELEVANT_MERGED: {
    label: {variant: 'done', text: 'Merged'},
    icon: {type: 'icon', icon: GitMergeIcon, className: 'color-fg-done', ariaLabel: 'Already merged'},
  },
  IRRELEVANT_AFTER: {
    label: null,
    icon: {
      type: 'icon',
      icon: GitPullRequestIcon,
      className: 'color-fg-muted',
      ariaLabel: 'Will not merge: higher up the stack',
    },
  },
  BLOCKED_STACK: {
    label: {variant: 'attention', text: 'Needs rebase'},
    icon: {type: 'icon', icon: AlertIcon, className: 'color-fg-attention', ariaLabel: 'Cannot merge'},
  },
  OK: {
    label: {variant: 'success', text: 'Ready to merge'},
    icon: {type: 'icon', icon: CheckCircleFillIcon, className: 'color-fg-success', ariaLabel: 'Ready to merge'},
  },
  LOADING_MERGE_STATE: {
    label: null,
    icon: {type: 'spinner'},
  },
  ERROR_MERGE_STATE: {
    label: {variant: 'attention', text: 'Error loading data'},
    icon: {
      type: 'icon',
      icon: AlertIcon,
      className: 'color-fg-attention',
      ariaLabel: 'Cannot load mergeability data',
    },
  },
  BLOCKED_MERGE_STATE: {
    label: {variant: 'attention', text: 'Failing rules'},
    icon: {type: 'icon', icon: AlertIcon, className: 'color-fg-attention', ariaLabel: 'Cannot merge'},
  },
  UNKNOWN_MERGE_STATE: {
    label: {variant: 'default', text: 'Checking ability to merge automatically'},
    icon: {type: 'spinner'},
  },
}

function EntryStatusLabel({
  entry,
  rebaseState,
}: {
  entry: StackSectionEntryType
  rebaseState?: StackRebasePullRequestStatus
}) {
  // Rebasing
  if (rebaseState === 'SUCCEEDED') {
    return <Label variant="success">Rebased</Label>
  } else if (rebaseState === 'CONFLICTED') {
    return <Label variant="danger">Merge conflicts</Label>
  } else if (rebaseState === 'FAILED') {
    return <Label variant="danger">Rebase failed</Label>
  } else if (rebaseState === 'PENDING') {
    return <Label variant="default">Rebasing…</Label>
  }

  const {label} = ENTRY_STATUS_INFO[entry.status]
  if (!label) return null
  return <Label variant={label.variant}>{label.text}</Label>
}

function EntryStatusIcon({
  entry,
  rebaseState,
}: {
  entry: StackSectionEntryType
  rebaseState?: StackRebasePullRequestStatus
}) {
  // Rebasing
  if (rebaseState === 'PENDING') {
    return <Spinner size="small" style={{marginLeft: -2}} />
  } else if (rebaseState === 'SUCCEEDED') {
    return <CheckCircleFillIcon size={12} className="color-fg-success" aria-label="Rebased successfully" />
  } else if (rebaseState === 'CONFLICTED') {
    return <AlertIcon size={12} className="color-fg-attention" aria-label="Merge conflicts after rebasing" />
  } else if (rebaseState === 'FAILED') {
    return <AlertIcon size={12} className="color-fg-attention" aria-label="Rebase failed" />
  }

  const {icon} = ENTRY_STATUS_INFO[entry.status]
  if (icon.type === 'spinner') {
    return <Spinner size="small" style={{marginLeft: -2}} />
  }
  const EntryIcon = icon.icon
  return <EntryIcon size={12} className={icon.className} aria-label={icon.ariaLabel} />
}

function StatusIcon({status}: {status: StackSectionStatusType}) {
  let className
  let Icon
  switch (status) {
    case 'BLOCKED':
    case 'FAILED':
      Icon = XIcon
      className = 'bgColor-danger-emphasis fgColor-onEmphasis'
      break
    case 'PASSED':
      Icon = CheckIcon
      className = 'bgColor-success-emphasis fgColor-onEmphasis'
      break
    case 'UNKNOWN':
    default:
      Icon = ExclamationIcon
      className = 'bgColor-muted fgColor-onEmphasis'
      break
  }

  return <CircleOcticon className={className} size={HEADER_ICON_SIZE} icon={() => <Icon size={16} />} />
}

function RebaseSummaryBanner({
  rebaseState,
  onDismiss,
}: {
  rebaseState: Record<number, StackRebasePullRequestStatus>
  onDismiss: () => void
}) {
  const states = Object.values(rebaseState)
  const succeededCount = states.filter(s => s === 'SUCCEEDED').length
  const failedCount = states.filter(s => s === 'FAILED' || s === 'CONFLICTED').length

  if (states.length === 0) return null

  const allSucceeded = failedCount === 0
  const variant = allSucceeded ? 'success' : 'critical'

  let description: string
  if (allSucceeded) {
    description = `Successfully rebased ${succeededCount} pull request${succeededCount === 1 ? '' : 's'}.`
  } else {
    const parts: string[] = []
    if (succeededCount > 0) {
      parts.push(`${succeededCount} pull request${succeededCount === 1 ? '' : 's'} rebased successfully.`)
    }
    parts.push(`${failedCount} pull request${failedCount === 1 ? '' : 's'} failed to rebase.`)
    description = parts.join(' ')
  }

  return (
    <Banner
      className="tmp-m-3"
      aria-label="Rebase summary"
      variant={variant}
      title="Rebase complete"
      hideTitle
      description={description}
      onDismiss={onDismiss}
    />
  )
}
